// src/components/common/chat/ChatImageModal.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../../../css/common/chat/ChatImageModal.css';
import { downloadBlobFromUrl } from '../../../../api/storage';
import DsModal from '../../DsModal';
import { buildImageDownloadName, getImageGroupKey } from './chatImageGroupUtils';

const noop = () => {};
const SWIPE_THRESHOLD = 40;
const SWIPE_DOMINANCE = 1.2;
const MIN_SCALE = 1;
const MAX_SCALE = 6;
const KEYBOARD_SCALE_STEP = 1.18;

const clampScale = (value) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
const touchDistance = (left, right) => Math.hypot(left.clientX - right.clientX, left.clientY - right.clientY);
const touchMidpoint = (left, right) => ({
  x: (left.clientX + right.clientX) / 2,
  y: (left.clientY + right.clientY) / 2,
});

const ChatImageModal = ({
  isOpen,
  src,
  fallbackSrc = '',
  alt = 'Preview',
  onClose = noop,
  onPrev,
  onNext,
  showNav = true,
  downloadItems = [],
  currentIndex = 0,
  onTouchStart,
  onTouchEnd,
  closeOnBack = true,
}) => {
  const viewportRef = useRef(null);
  const touchStartRef = useRef(null);
  const touchGestureRef = useRef(null);
  const pointerDragRef = useRef(null);
  const dragMovedRef = useRef(false);
  // wheel / touchmove は preventDefault が必要。React の onWheel/onTouchMove は passive で
  // 登録されるため preventDefault が警告を大量に出す。ネイティブの非passiveリスナーで束ねる。
  const handleWheelRef = useRef(noop);
  const handleTouchMoveRef = useRef(noop);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [activeSrc, setActiveSrc] = useState(src);
  const canNavigate = showNav && typeof onPrev === 'function' && typeof onNext === 'function';
  const hasExternalTouchHandlers = typeof onTouchStart === 'function' || typeof onTouchEnd === 'function';
  const currentDownloadItem = Array.isArray(downloadItems) ? downloadItems[currentIndex] : null;
  const currentGroupItems = useMemo(() => {
    if (!currentDownloadItem) return [];
    const groupKey = getImageGroupKey(currentDownloadItem);
    if (!groupKey.startsWith('group:')) return [currentDownloadItem];
    return downloadItems
      .filter((item) => getImageGroupKey(item) === groupKey)
      .slice()
      .sort((left, right) => (Number(left?.image_group_order) || 0) - (Number(right?.image_group_order) || 0));
  }, [currentDownloadItem, downloadItems]);
  const canDownload = Boolean(currentDownloadItem?.downloadUrl || currentDownloadItem?.fullSrc || src);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (isOpen) resetView();
  }, [isOpen, src, resetView]);

  useEffect(() => {
    setActiveSrc(src);
  }, [src]);

  const zoomAt = useCallback((nextScaleValue, center = null) => {
    setScale((currentScale) => {
      const nextScale = clampScale(nextScaleValue);
      if (Math.abs(nextScale - MIN_SCALE) < 0.001) {
        setOffset({ x: 0, y: 0 });
        return MIN_SCALE;
      }

      const rect = viewportRef.current?.getBoundingClientRect();
      const point = rect && center
        ? { x: center.x - rect.left - rect.width / 2, y: center.y - rect.top - rect.height / 2 }
        : { x: 0, y: 0 };
      const ratio = nextScale / currentScale;
      setOffset((currentOffset) => ({
        x: point.x - (point.x - currentOffset.x) * ratio,
        y: point.y - (point.y - currentOffset.y) * ratio,
      }));
      return nextScale;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key === '+' || event.key === '=' || event.key === 'Add') {
        event.preventDefault();
        zoomAt(scale * KEYBOARD_SCALE_STEP);
      } else if (event.key === '-' || event.key === '_' || event.key === 'Subtract') {
        event.preventDefault();
        zoomAt(scale / KEYBOARD_SCALE_STEP);
      } else if (event.key === '0') {
        event.preventDefault();
        resetView();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, resetView, scale, zoomAt]);

  // wheel / touchmove を非passiveで直接束ねる（preventDefault を許可し、コンソール警告を防ぐ）。
  useEffect(() => {
    if (!isOpen) return undefined;
    const target = viewportRef.current?.closest('.ds-modal') || viewportRef.current;
    if (!target) return undefined;
    const onWheelNative = (event) => handleWheelRef.current(event);
    const onTouchMoveNative = (event) => handleTouchMoveRef.current(event);
    target.addEventListener('wheel', onWheelNative, { passive: false });
    target.addEventListener('touchmove', onTouchMoveNative, { passive: false });
    return () => {
      target.removeEventListener('wheel', onWheelNative);
      target.removeEventListener('touchmove', onTouchMoveNative);
    };
  }, [isOpen]);

  if (!isOpen || !src) return null;

  const panBy = (dx, dy) => {
    if (scale <= 1) return;
    setOffset((currentOffset) => ({
      x: currentOffset.x + dx,
      y: currentOffset.y + dy,
    }));
  };

  const handleWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (downloadMenuOpen) return;
    if (scale > 1 && !e.ctrlKey && !e.metaKey && (Math.abs(e.deltaX) > 0 || Math.abs(e.deltaY) < 45)) {
      panBy(-e.deltaX, -e.deltaY);
      return;
    }
    const direction = e.deltaY < 0 ? 1 : -1;
    const factor = direction > 0 ? 1.12 : 1 / 1.12;
    zoomAt(scale * factor, { x: e.clientX, y: e.clientY });
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0 || scale <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    dragMovedRef.current = false;
    pointerDragRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = pointerDragRef.current;
    if (!drag || scale <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) dragMovedRef.current = true;
    pointerDragRef.current = { x: e.clientX, y: e.clientY };
    panBy(dx, dy);
  };

  const handlePointerUp = (e) => {
    if (!pointerDragRef.current) return;
    pointerDragRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const handleTouchStart = (e) => {
    if (typeof onTouchStart === 'function') onTouchStart(e);
    if (hasExternalTouchHandlers) return;
    if (e.touches?.length >= 2) {
      const [first, second] = e.touches;
      touchGestureRef.current = {
        type: 'pinch',
        distance: touchDistance(first, second),
        scale,
        midpoint: touchMidpoint(first, second),
      };
      return;
    }
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchGestureRef.current = scale > 1 ? { type: 'pan', x: touch.clientX, y: touch.clientY } : null;
  };

  const handleTouchMove = (e) => {
    if (hasExternalTouchHandlers) return;
    if (e.touches?.length >= 2) {
      e.preventDefault();
      const [first, second] = e.touches;
      const gesture = touchGestureRef.current?.type === 'pinch'
        ? touchGestureRef.current
        : { type: 'pinch', distance: touchDistance(first, second), scale, midpoint: touchMidpoint(first, second) };
      touchGestureRef.current = gesture;
      const nextDistance = touchDistance(first, second);
      const midpoint = touchMidpoint(first, second);
      zoomAt(gesture.scale * (nextDistance / Math.max(gesture.distance, 1)), midpoint);
      return;
    }
    const gesture = touchGestureRef.current;
    const touch = e.touches && e.touches[0];
    if (gesture?.type !== 'pan' || !touch || scale <= 1) return;
    e.preventDefault();
    panBy(touch.clientX - gesture.x, touch.clientY - gesture.y);
    touchGestureRef.current = { type: 'pan', x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (typeof onTouchEnd === 'function') onTouchEnd(e);
    if (hasExternalTouchHandlers) return;
    if (touchGestureRef.current?.type === 'pinch' || touchGestureRef.current?.type === 'pan') {
      touchGestureRef.current = null;
      return;
    }
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start || !canNavigate) return;
    const touch = e.changedTouches && e.changedTouches[0];
    if (!touch) return;
    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absX < SWIPE_THRESHOLD || absX < absY * SWIPE_DOMINANCE) return;
    if (dx < 0) onNext();
    else onPrev();
  };

  const handleContentClick = (e) => {
    if (dragMovedRef.current) {
      dragMovedRef.current = false;
      return;
    }
    // 画像・各ボタンは stopPropagation 済みなので、ここに来るのは画像まわりの
    // 余白領域（カード / body / ビューポートの letterbox 部分）のクリックのみ。
    // フルスクリーン時、画像の左右上下に広がる余白＝ viewport 要素なので、これも
    // 閉じる対象に含める（縦長の宣材写真などで「写真以外」を押しても閉じるように）。
    if (
      e.target === e.currentTarget ||
      e.target?.classList?.contains('ds-modal__body') ||
      e.target?.classList?.contains('chat-image-modal__viewport')
    ) {
      e.stopPropagation();
      onClose();
    }
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  const handlePrevClick = (e) => {
    e.stopPropagation();
    if (canNavigate) onPrev();
  };

  const handleNextClick = (e) => {
    e.stopPropagation();
    if (canNavigate) onNext();
  };

  const downloadOne = async (item, index = 0) => {
    const url = item?.downloadUrl || item?.fullSrc || item?.src || src;
    if (!url) return false;
    try {
      const blob = await downloadBlobFromUrl(url, 'include');
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = buildImageDownloadName(item, index);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      return true;
    } catch {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = buildImageDownloadName(item, index);
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return true;
    }
  };

  const handleDownloadCurrent = async () => {
    if (!canDownload || downloadProgress) return;
    setDownloadMenuOpen(false);
    setDownloadProgress({ current: 0, total: 1 });
    await downloadOne(currentDownloadItem || { fullSrc: src }, currentIndex);
    setDownloadProgress(null);
  };

  const handleDownloadAll = async () => {
    if (!canDownload || downloadProgress) return;
    const items = currentGroupItems.length ? currentGroupItems : [currentDownloadItem || { fullSrc: src }];
    setDownloadMenuOpen(false);
    for (let index = 0; index < items.length; index += 1) {
      setDownloadProgress({ current: index + 1, total: items.length });
      // eslint-disable-next-line no-await-in-loop
      await downloadOne(items[index], index);
    }
    setDownloadProgress(null);
  };

  const handleDownloadClick = (e) => {
    e.stopPropagation();
    if (!canDownload || downloadProgress) return;
    if (currentGroupItems.length > 1) {
      setDownloadMenuOpen((open) => !open);
      return;
    }
    handleDownloadCurrent();
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
  };

  // ネイティブ非passiveリスナーが常に最新のハンドラ（最新の state を参照）を呼べるようにする。
  handleWheelRef.current = handleWheel;
  handleTouchMoveRef.current = handleTouchMove;

  return (
    <DsModal
      open={isOpen}
      onClose={onClose}
      ariaLabel={alt || '画像プレビュー'}
      showClose={false}
      closeOnBack={closeOnBack}
      className="chat-image-modal__content"
      overlayClassName="chat-image-modal__overlay"
      onClick={handleContentClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
        <button className="chat-image-modal__close" type="button" onClick={handleCloseClick} aria-label="Close">
          &times;
        </button>
        {canDownload ? (
          <div className="chat-image-modal__downloadArea">
            <button
              className="chat-image-modal__download"
              type="button"
              onClick={handleDownloadClick}
              disabled={Boolean(downloadProgress)}
              aria-label="画像をダウンロード"
            >
              {downloadProgress ? `${downloadProgress.current}/${downloadProgress.total}` : (
                <i className="fa-solid fa-download" aria-hidden="true" />
              )}
            </button>
            {downloadMenuOpen ? (
              <div
                className="chat-image-modal__downloadMenu ds-compact-action-menu"
                onClick={(e) => e.stopPropagation()}
                role="menu"
                aria-label="画像ダウンロード方法を選択"
              >
                <button type="button" role="menuitem" onClick={handleDownloadCurrent}>この画像だけ</button>
                <button type="button" role="menuitem" onClick={handleDownloadAll}>全てダウンロード</button>
              </div>
            ) : null}
          </div>
        ) : null}
        {canNavigate && (
          <button
            className="chat-image-modal__nav chat-image-modal__prev"
            type="button"
            onClick={handlePrevClick}
            aria-label="Previous"
          >
            &lt;
          </button>
        )}
        <div className="chat-image-modal__viewport" ref={viewportRef}>
          <img
            className="chat-image-modal__img"
            src={activeSrc || src}
            alt={alt}
            onError={() => {
              if (fallbackSrc && activeSrc !== fallbackSrc) {
                setActiveSrc(fallbackSrc);
              }
            }}
            onClick={handleImageClick}
            draggable={false}
            style={{
              transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
              cursor: scale > 1 ? (pointerDragRef.current ? 'grabbing' : 'grab') : 'zoom-in',
            }}
          />
        </div>
        {canNavigate && (
          <button
            className="chat-image-modal__nav chat-image-modal__next"
            type="button"
            onClick={handleNextClick}
            aria-label="Next"
          >
            &gt;
          </button>
        )}
    </DsModal>
  );
};

export default ChatImageModal;
